import { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Theme,
  Typography,
} from '@mui/material';
import { makeStyles } from 'tss-react/mui';

const useStyles = makeStyles()((theme: Theme) => ({
  dialog: {
    '& .MuiDialog-paper': {
      width: '100%',
      maxWidth: '480px',
      padding: '24px',
      gap: '16px',
      border: `1px solid ${theme.palette.secondary.dark}`,
      borderRadius: '16px',
      backgroundColor: theme.palette.primary.light,
    },
  },
  title: {
    padding: 0,
    color: theme.palette.common.white,
    fontFamily: 'Poppins',
    fontSize: '22px',
    fontWeight: 600,
  },
  subtitle: {
    marginTop: '8px',
    color: theme.palette.text.disabled,
    fontFamily: 'Inter',
    fontSize: '14px',
    fontWeight: 400,
  },
  content: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    padding: '16px 0 0',
  },
  fieldWrapper: {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
  },
  fieldLabel: {
    color: theme.palette.text.disabled,
    fontFamily: 'Inter',
    fontSize: '13px',
    fontWeight: 500,
    lineHeight: 1.4,
  },
  field: {
    '& .MuiOutlinedInput-root': {
      color: theme.palette.common.white,
      borderRadius: '8px',
      background: theme.palette.primary.dark,
      '& fieldset': { borderColor: theme.palette.secondary.dark },
      '&:hover fieldset, &.Mui-focused fieldset': { borderColor: theme.palette.info.light },
    },
    '& .MuiOutlinedInput-input': {
      padding: '11px 14px',
      fontFamily: 'Inter',
      fontSize: '14px',
      fontWeight: 500,
    },
    '& .MuiFormHelperText-root': {
      marginLeft: '2px',
      marginRight: '2px',
    },
  },
  actions: {
    gap: '12px',
    padding: '8px 0 0',
  },
}));

interface EditDraftSettlementAmountsPopupProps {
  isOpen: boolean;
  onClose: () => void;
  onApply: (amounts: { settledAmount: number; pipAmount: number }) => void;
  showPipAmount: boolean;
  initialSettlementAmount: number;
  initialPipAmount: number;
}

const EditDraftSettlementAmountsPopup = ({
  isOpen,
  onClose,
  onApply,
  showPipAmount,
  initialSettlementAmount,
  initialPipAmount,
}: EditDraftSettlementAmountsPopupProps) => {
  const { classes } = useStyles();
  const [settlementAmount, setSettlementAmount] = useState('');
  const [pipAmount, setPipAmount] = useState('');

  useEffect(() => {
    if (isOpen) {
      setSettlementAmount(String(initialSettlementAmount));
      setPipAmount(String(initialPipAmount));
    }
  }, [isOpen, initialSettlementAmount, initialPipAmount]);

  const settlementAmountValue = Number(settlementAmount);
  const pipAmountValue = Number(pipAmount);
  const isSettlementAmountInvalid =
    settlementAmount.trim() === '' ||
    !Number.isFinite(settlementAmountValue) ||
    settlementAmountValue < 0 ||
    (!showPipAmount && settlementAmountValue === 0);
  const isPipAmountInvalid =
    showPipAmount &&
    (pipAmount.trim() === '' || !Number.isFinite(pipAmountValue) || pipAmountValue <= 0);

  const handleApply = () => {
    if (isSettlementAmountInvalid || isPipAmountInvalid) return;
    onApply({
      settledAmount: Math.round(settlementAmountValue * 100) / 100,
      pipAmount: showPipAmount ? Math.round(pipAmountValue * 100) / 100 : 0,
    });
    onClose();
  };

  return (
    <Dialog className={classes.dialog} open={isOpen} onClose={onClose}>
      <DialogTitle className={classes.title}>
        Edit amounts
        <Typography className={classes.subtitle}>
          The memo attorney fee stays fixed. Changing amounts recalculates the net settlement.
        </Typography>
      </DialogTitle>
      <DialogContent className={classes.content}>
        <Box className={classes.fieldWrapper}>
          <Typography className={classes.fieldLabel}>Settlement amount</Typography>
          <TextField
            className={classes.field}
            aria-label="Settlement amount"
            type="number"
            inputProps={{ min: 0, step: 0.01 }}
            value={settlementAmount}
            onChange={(event) => setSettlementAmount(event.target.value)}
            error={isSettlementAmountInvalid}
            helperText={
              isSettlementAmountInvalid
                ? showPipAmount
                  ? 'Enter a non-negative settlement amount.'
                  : 'Enter a settlement amount greater than zero.'
                : undefined
            }
            fullWidth
            size="small"
          />
        </Box>
        {showPipAmount && (
          <Box className={classes.fieldWrapper}>
            <Typography className={classes.fieldLabel}>PIP amount</Typography>
            <TextField
              className={classes.field}
              aria-label="PIP amount"
              type="number"
              inputProps={{ min: 0.01, step: 0.01 }}
              value={pipAmount}
              onChange={(event) => setPipAmount(event.target.value)}
              error={isPipAmountInvalid}
              helperText={isPipAmountInvalid ? 'Enter a PIP amount greater than zero.' : undefined}
              fullWidth
              size="small"
            />
          </Box>
        )}
      </DialogContent>
      <DialogActions className={classes.actions}>
        <Button onClick={onClose} sx={{ color: 'text.disabled', textTransform: 'none' }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleApply}
          disabled={isSettlementAmountInvalid || isPipAmountInvalid}
          sx={{ textTransform: 'none' }}
        >
          Apply
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditDraftSettlementAmountsPopup;
